import { bool, func, instanceOf, object, shape, string } from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { isScrollingDisabled } from '../../ducks/UI.duck';
import { savePaymentMethod } from '../../ducks/paymentMethods.duck';
import { confirmCardPayment, handleCardSetup } from '../../ducks/stripe.duck';
import routeConfiguration from '../../routeConfiguration';
import {
  txIsInitiatePayment,
  txIsPaymentExpired,
  txIsPaymentPending,
} from '../../util/customTransaction/mainRentalTransaction';
import {
  ensureCurrentUser,
  ensurePaymentMethodCard,
  ensureStripeCustomer,
  ensureTransaction,
} from '../../util/data';
import {
  dateFromAPIToLocalNoon,
  dateFromLocalToAPI,
  getDateForEmail,
  minutesBetween,
} from '../../util/dates';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { findRouteByRouteName, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';

import isEqual from 'lodash/isEqual';
import moment from 'moment';
import { types as sdkTypes } from '../../util/sdkLoader';
import { savePhoneNumber } from '../ContactDetailsPage/ContactDetailsPage.duck';
import { createStripeSetupIntent } from '../PaymentMethodsPage/PaymentMethodsPage.duck';
import CheckoutPage from './CheckoutPage';
import {
  confirmPayment,
  initiateOrder,
  initiatePayment,
  saveLast4DigitsCard,
  sendMessage,
  setInitialValues,
  speculateTransaction,
  stripeCustomer,
} from './CheckoutPage.duck';
import { clearData, storeData, storedData } from './CheckoutPageSessionHelpers';

const { Money } = sdkTypes;

const STORAGE_KEY = 'CheckoutPage';

// Payment charge options
const ONETIME_PAYMENT = 'ONETIME_PAYMENT';
const PAY_AND_SAVE_FOR_LATER_USE = 'PAY_AND_SAVE_FOR_LATER_USE';
const USE_SAVED_CARD = 'USE_SAVED_CARD';

const STRIPE_PI_USER_ACTIONS_DONE_STATUSES = ['processing', 'requires_capture', 'succeeded'];

const paymentFlow = (selectedPaymentMethod, saveAfterOnetimePayment) => {
  // Payment mode could be 'replaceCard', but without explicit saveAfterOnetimePayment flag,
  // we'll handle it as one-time payment
  return selectedPaymentMethod === 'defaultCard'
    ? USE_SAVED_CARD
    : saveAfterOnetimePayment
    ? PAY_AND_SAVE_FOR_LATER_USE
    : ONETIME_PAYMENT;
};

const initializeOrderPage = (initialValues, routes, dispatch) => {
  const OrderPage = findRouteByRouteName('OrderDetailsPage', routes);

  // Transaction is already created, but if the initial message
  // sending failed, we tell it to the OrderDetailsPage.
  dispatch(OrderPage.setInitialValues(initialValues));
};

const checkIsPaymentExpired = existingTransaction => {
  return txIsPaymentExpired(existingTransaction)
    ? true
    : txIsInitiatePayment(existingTransaction)
    ? minutesBetween(existingTransaction.attributes.lastTransitionedAt, new Date()) >= 15
    : false;
};

const getClientSecret = setupIntent => {
  return setupIntent && setupIntent.attributes ? setupIntent.attributes.clientSecret : null;
};

export class MainCheckoutPageComponent extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pageData: {},
      dataLoaded: false,
      submitting: false,
    };

    this.loadInitialData = this.loadInitialData.bind(this);
    this.handlePaymentIntent = this.handlePaymentIntent.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleConfirmPayment = this.handleConfirmPayment.bind(this);
  }

  componentDidMount() {
    if (window) {
      this.loadInitialData();
    }
  }

  loadInitialData() {
    const {
      bookingData,
      bookingDates,
      listing,
      transaction,
      fetchSpeculatedTransaction,
      fetchStripeCustomer,
      history,
    } = this.props;

    // Fetch currentUser with stripeCustomer entity
    // Note: since there's need for data loading in "componentWillMount" function,
    //       this is added here instead of loadData static function.
    fetchStripeCustomer();

    // Browser's back navigation should not rewrite data in session store.
    // Action is 'POP' on both history.back() and page refresh cases.
    // Action is 'PUSH' when user has directed through a link
    // Action is 'REPLACE' when user has directed through login/signup process
    const hasNavigatedThroughLink = history.action === 'PUSH' || history.action === 'REPLACE';

    const hasDataInProps = !!(bookingData && bookingDates && listing) && hasNavigatedThroughLink;
    if (hasDataInProps) {
      // Store data only if data is passed through props and user has navigated through a link.
      storeData(bookingData, bookingDates, listing, transaction, STORAGE_KEY);
    }

    // NOTE: stored data can be empty if user has already successfully completed transaction.
    const pageData = hasDataInProps
      ? { bookingData, bookingDates, listing, transaction }
      : storedData(STORAGE_KEY);

    // Check if a booking is already created according to stored data.
    const tx = pageData ? pageData.transaction : null;
    const isBookingCreated = tx && tx.booking && tx.booking.id;

    const shouldFetchSpeculatedTransaction =
      pageData &&
      pageData.listing &&
      pageData.listing.id &&
      pageData.bookingData &&
      pageData.bookingDates &&
      pageData.bookingDates.bookingStart &&
      pageData.bookingDates.bookingEnd &&
      !isBookingCreated;

    if (shouldFetchSpeculatedTransaction) {
      const listingId = pageData.listing.id;
      const transactionId = tx ? tx.id : null;
      const { bookingStart, bookingEnd } = pageData.bookingDates;
      const { delivery, isNegotiate, deliveryAddress, payoutTotal, seats } = pageData.bookingData;
      const { attributes } = pageData.listing;

      const { bondFee, deliveryFee } = attributes ? attributes.publicData : {};

      const bondFeeBySeats = {
        amount: bondFee.amount * parseInt(seats),
        currency: bondFee.currency,
      };

      // Convert picked date to date that will be converted on the API as
      // a noon of correct year-month-date combo in UTC
      const bookingStartForAPI = dateFromLocalToAPI(bookingStart);
      const bookingEndForAPI = dateFromLocalToAPI(bookingEnd);

      // Fetch speculated transaction for showing price in booking breakdown
      // NOTE: if unit type is line-item/units, quantity needs to be added.
      // The way to pass it to checkout page is through pageData.bookingData
      fetchSpeculatedTransaction(
        {
          listingId,
          bookingStart: bookingStartForAPI,
          bookingEnd: bookingEndForAPI,
          bookingData: {
            startDate: isNegotiate ? bookingStart.getTime() : bookingStartForAPI,
            endDate: isNegotiate ? bookingEnd.getTime() : bookingEndForAPI,
            delivery,
            isNegotiate,
            payoutTotal,
            seats: parseInt(seats),
          },
          protectedData: {
            bondFee: bondFeeBySeats,
            deliveryData: {
              delivery,
              deliveryAddress: deliveryAddress ? JSON.parse(deliveryAddress) : null,
              deliveryFee,
            },
            seats: parseInt(seats),
          },
        },
        transactionId
      );
    }

    this.setState({ pageData: pageData || {}, dataLoaded: true });
  }

  handleConfirmPayment(handlePaymentParams) {
    const {
      currentUser,
      stripeCustomerFetched,
      onConfirmCardPayment,
      onConfirmPayment,
      onSendMessage,
      onSavePaymentMethod,
      onCreateSetupIntent,
      onHandleCardSetup,
      onInitiatePayment,
    } = this.props;
    const {
      pageData,
      message,
      selectedPaymentMethod,
      saveAfterOnetimePayment,
    } = handlePaymentParams;
    const storedTx = ensureTransaction(pageData.transaction);

    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const ensuredStripeCustomer = ensureStripeCustomer(ensuredCurrentUser.stripeCustomer);
    const ensuredDefaultPaymentMethod = ensurePaymentMethodCard(
      ensuredStripeCustomer.defaultPaymentMethod
    );

    const hasDefaultPaymentMethod = !!(
      stripeCustomerFetched &&
      ensuredStripeCustomer.attributes.stripeCustomerId &&
      ensuredDefaultPaymentMethod.id
    );

    const stripePaymentMethodId = hasDefaultPaymentMethod
      ? ensuredDefaultPaymentMethod.attributes.stripePaymentMethodId
      : null;

    const selectedPaymentFlow = paymentFlow(selectedPaymentMethod, saveAfterOnetimePayment);

    const fnInitiatePayment = fnParams => {
      const hasPaymentIntents =
        storedTx.attributes.protectedData && storedTx.attributes.protectedData.stripePaymentIntents;
      // If paymentIntent exists, order has been initiated previously.
      return hasPaymentIntents
        ? Promise.resolve(storedTx)
        : onInitiatePayment(fnParams, storedTx.id);
    };

    // Step 2: pay using Stripe SDK
    const fnConfirmCardPayment = fnParams => {
      // fnParams should be returned transaction entity

      const order = ensureTransaction(fnParams);
      if (order.id) {
        // Store order.
        const { bookingData, bookingDates, listing } = pageData;
        storeData(bookingData, bookingDates, listing, order, STORAGE_KEY);
        this.setState({ pageData: { ...pageData, transaction: order } });
      }

      const hasPaymentIntents =
        order.attributes.protectedData && order.attributes.protectedData.stripePaymentIntents;

      const { stripePaymentIntentClientSecret } = hasPaymentIntents
        ? order.attributes.protectedData.stripePaymentIntents.default
        : null;

      const { stripe, card, billingDetails, paymentIntent } = handlePaymentParams;
      const stripeElementMaybe = selectedPaymentFlow !== USE_SAVED_CARD ? { card } : {};

      const paymentParams =
        selectedPaymentFlow !== USE_SAVED_CARD
          ? {
              payment_method: {
                billing_details: billingDetails,
                card: card,
              },
            }
          : { payment_method: stripePaymentMethodId };

      const params = {
        stripePaymentIntentClientSecret,
        orderId: order.id,
        stripe,
        ...stripeElementMaybe,
        paymentParams,
      };

      // If paymentIntent status is not waiting user action,
      // confirmCardPayment has been called previously.
      const hasPaymentIntentUserActionsDone =
        paymentIntent && STRIPE_PI_USER_ACTIONS_DONE_STATUSES.includes(paymentIntent.status);

      return hasPaymentIntentUserActionsDone
        ? Promise.resolve({ transactionId: order.id, paymentIntent })
        : onConfirmCardPayment(params);
    };

    // Step 3: complete order by confirming payment to Marketplace API
    // Parameter should contain { paymentIntent, transactionId } returned in step 2
    const fnConfirmPayment = fnParams => {
      return onConfirmPayment(fnParams);
    };

    // Step 4: send initial message
    const fnSendMessage = fnParams => {
      return onSendMessage({ ...fnParams, message });
    };

    // Step 5: optionally save card as defaultPaymentMethod
    const fnSavePaymentMethod = fnParams => {
      if (selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE) {
        const { stripe, card, billingDetails } = handlePaymentParams;

        return onCreateSetupIntent()
          .then(setupIntent => {
            const stripeParams = {
              stripe,
              card,
              setupIntentClientSecret: getClientSecret(setupIntent),
              paymentParams: {
                payment_method_data: {
                  billing_details: billingDetails,
                },
              },
            };

            return onHandleCardSetup(stripeParams);
          })
          .then(result => {
            const newPaymentMethod = result.setupIntent.payment_method;
            return onSavePaymentMethod(ensuredStripeCustomer, newPaymentMethod);
          })
          .then(response => {
            if (response.errors) {
              return { ...fnParams, paymentMethodSaved: false };
            }
            return { ...fnParams, paymentMethodSaved: true };
          })
          .catch(e => {
            // Real error cases are catched already in paymentMethods page.
            return { ...fnParams, paymentMethodSaved: false };
          });
      } else {
        return Promise.resolve({ ...fnParams, paymentMethodSaved: true });
      }
    };

    const applyAsync = (acc, val) => acc.then(val);
    const composeAsync = (...funcs) => x => funcs.reduce(applyAsync, Promise.resolve(x));
    const handlePaymentIntentCreation = composeAsync(
      fnInitiatePayment,
      fnConfirmCardPayment,
      fnConfirmPayment,
      fnSendMessage,
      fnSavePaymentMethod
    );

    const optionalPaymentParams =
      selectedPaymentFlow === USE_SAVED_CARD && hasDefaultPaymentMethod
        ? { paymentMethod: stripePaymentMethodId }
        : selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE
        ? { setupPaymentMethodForSaving: true }
        : {};

    const orderParams = {
      ...optionalPaymentParams,
    };

    return handlePaymentIntentCreation(orderParams);
  }

  handlePaymentIntent(handlePaymentParams) {
    const {
      currentUser,
      stripeCustomerFetched,
      onInitiateOrder,
      onSendMessage,
      onSavePaymentMethod,
      onCreateSetupIntent,
      onHandleCardSetup,
    } = this.props;
    const {
      pageData,
      speculatedTransaction,
      message,
      selectedPaymentMethod,
      saveAfterOnetimePayment,
      phoneNumber,
    } = handlePaymentParams;
    const storedTx = ensureTransaction(pageData.transaction);

    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const ensuredStripeCustomer = ensureStripeCustomer(ensuredCurrentUser.stripeCustomer);
    const ensuredDefaultPaymentMethod = ensurePaymentMethodCard(
      ensuredStripeCustomer.defaultPaymentMethod
    );

    const hasDefaultPaymentMethod = !!(
      stripeCustomerFetched &&
      ensuredStripeCustomer.attributes.stripeCustomerId &&
      ensuredDefaultPaymentMethod.id
    );
    const stripePaymentMethodId = hasDefaultPaymentMethod
      ? ensuredDefaultPaymentMethod.attributes.stripePaymentMethodId
      : null;

    const selectedPaymentFlow = paymentFlow(selectedPaymentMethod, saveAfterOnetimePayment);

    // Step 1: initiate order by requesting payment from Marketplace API
    const fnRequestPayment = fnParams => {
      // fnParams should be { listingId, bookingStart, bookingEnd }
      const hasPaymentIntents =
        storedTx.attributes.protectedData && storedTx.attributes.protectedData.stripePaymentIntents;

      // If paymentIntent exists, order has been initiated previously.
      return hasPaymentIntents ? Promise.resolve(storedTx) : onInitiateOrder(fnParams, storedTx.id);
    };

    // Step 2: send initial message
    const fnSendMessage = fnParams => {
      return onSendMessage({ ...fnParams, message });
    };

    // Step 3: optionally save card as defaultPaymentMethod
    const fnSavePaymentMethod = fnParams => {
      if (selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE) {
        const { stripe, card, billingDetails } = handlePaymentParams;

        return onCreateSetupIntent()
          .then(setupIntent => {
            const stripeParams = {
              stripe,
              card,
              setupIntentClientSecret: getClientSecret(setupIntent),
              paymentParams: {
                payment_method_data: {
                  billing_details: billingDetails,
                },
              },
            };

            return onHandleCardSetup(stripeParams);
          })
          .then(result => {
            const newPaymentMethod = result.setupIntent.payment_method;
            return onSavePaymentMethod(ensuredStripeCustomer, newPaymentMethod);
          })
          .then(response => {
            if (response.errors) {
              return { ...fnParams, paymentMethodSaved: false };
            }
            return { ...fnParams, paymentMethodSaved: true };
          })
          .catch(e => {
            // Real error cases are catched already in paymentMethods page.
            return { ...fnParams, paymentMethodSaved: false };
          });
      } else {
        return Promise.resolve({ ...fnParams, paymentMethodSaved: true });
      }
    };

    // Here we create promise calls in sequence
    // This is pretty much the same as:
    // fnRequestPayment({...initialParams})
    //   .then(result => fnConfirmCardPayment({...result}))
    //   .then(result => fnConfirmPayment({...result}))
    const applyAsync = (acc, val) => acc.then(val);
    const composeAsync = (...funcs) => x => funcs.reduce(applyAsync, Promise.resolve(x));
    const handlePaymentIntentCreation = composeAsync(
      fnRequestPayment,
      fnSendMessage,
      fnSavePaymentMethod
    );

    // Create order aka transaction
    // NOTE: if unit type is line-item/units, quantity needs to be added.
    // The way to pass it to checkout page is through pageData.bookingData
    const tx = speculatedTransaction ? speculatedTransaction : storedTx;

    // Note: optionalPaymentParams contains Stripe paymentMethod,
    // but that can also be passed on Step 2
    // stripe.confirmCardPayment(stripe, { payment_method: stripePaymentMethodId })
    const optionalPaymentParams =
      selectedPaymentFlow === USE_SAVED_CARD && hasDefaultPaymentMethod
        ? { paymentMethod: stripePaymentMethodId }
        : selectedPaymentFlow === PAY_AND_SAVE_FOR_LATER_USE
        ? { setupPaymentMethodForSaving: true }
        : {};

    const { delivery, deliveryAddress, isNegotiate, payoutTotal, seats } = pageData.bookingData;
    const { publicData } = pageData.listing.attributes;
    const { deliveryFee } = publicData;

    const startDate = dateFromAPIToLocalNoon(tx.booking.attributes.start);
    const endDate = dateFromAPIToLocalNoon(tx.booking.attributes.end);
    const threeDayBeforeStartDate = moment(startDate).subtract(3, 'days');
    const TODAY = moment();
    const chargeDate = threeDayBeforeStartDate.isBefore(TODAY) ? TODAY : threeDayBeforeStartDate;
    const chargedTime = getDateForEmail(chargeDate);
    const bondReleaseTime = moment(endDate).add(60, 'hours');

    const bookingDisplayStart = moment().isSame(startDate, 'day')
      ? new Date()
      : tx.booking.attributes.start;

    const orderParams = {
      listingId: pageData.listing.id,
      bookingStart: tx.booking.attributes.start,
      bookingEnd: tx.booking.attributes.end,
      bookingDisplayStart,
      bookingDisplayEnd: moment(tx.booking.attributes.end)
        .subtract(1, 'd')
        .endOf('day')
        .toDate(),
      bookingData: {
        startDate: isNegotiate ? startDate.getTime() : tx.booking.attributes.start,
        endDate: isNegotiate ? endDate.getTime() : tx.booking.attributes.end,
        delivery,
        isNegotiate,
        payoutTotal,
        seats: parseInt(seats),
      },
      protectedData: {
        currentYear: new Date().getFullYear().toString(),
        phoneNumber: phoneNumber,
        chargedTime,
        bondReleaseTime: getDateForEmail(bondReleaseTime, true),
        deliveryData: {
          delivery,
          deliveryAddress: deliveryAddress ? JSON.parse(deliveryAddress) : null,
          deliveryFee,
        },
        seats: parseInt(seats),
      },
      ...optionalPaymentParams,
    };

    return handlePaymentIntentCreation(orderParams);
  }

  handleSubmit(values) {
    if (this.state.submitting) {
      return;
    }
    this.setState({ submitting: true });

    const {
      history,
      speculatedTransaction,
      currentUser,
      paymentIntent,
      dispatch,
      onSavePhoneNumber,
      onSaveLast4DigitsCard,
    } = this.props;

    const { card, message, paymentMethod, stripe, formValues } = values;

    const {
      name,
      addressLine1,
      addressLine2,
      postal,
      city,
      state,
      country,
      saveAfterOnetimePayment,
      phoneNumber,
    } = formValues;

    // Billing address is recommended.
    // However, let's not assume that <StripePaymentAddress> data is among formValues.
    // Read more about this from Stripe's docs
    // https://stripe.com/docs/stripe-js/reference#stripe-handle-card-payment-no-element
    const addressMaybe =
      addressLine1 && postal
        ? {
            address: {
              city: city,
              country: country,
              line1: addressLine1,
              line2: addressLine2,
              postal_code: postal,
              state: state,
            },
          }
        : {};
    const billingDetails = {
      name,
      email: ensureCurrentUser(currentUser).attributes.email,
      ...addressMaybe,
    };

    const requestPaymentParams = {
      pageData: this.state.pageData,
      speculatedTransaction,
      stripe: stripe,
      card,
      billingDetails,
      message,
      paymentIntent,
      selectedPaymentMethod: paymentMethod,
      saveAfterOnetimePayment: !!saveAfterOnetimePayment,
      phoneNumber,
    };

    const storedTx = ensureTransaction(this.state.pageData.transaction);

    const shouldFetchPaymentIntent =
      storedTx.id &&
      storedTx.booking &&
      storedTx.booking.id &&
      txIsPaymentPending(storedTx) &&
      !checkIsPaymentExpired(storedTx);

    const handleRequest = shouldFetchPaymentIntent
      ? this.handleConfirmPayment
      : this.handlePaymentIntent;

    handleRequest(requestPaymentParams)
      .then(res => {
        const { orderId, messageSuccess, paymentMethodSaved } = res;
        this.setState({ submitting: false });

        const ensuredCurrentUser = ensureCurrentUser(currentUser);
        const protectedData = ensuredCurrentUser.attributes.profile;

        const { phoneNumber: currentPhoneNumber } = protectedData || {};
        if (phoneNumber && !isEqual(phoneNumber, currentPhoneNumber)) {
          onSavePhoneNumber({ phoneNumber });
        }

        if (paymentMethodSaved) {
          onSaveLast4DigitsCard(orderId.uuid);
        }

        const routes = routeConfiguration();
        const initialMessageFailedToTransaction = messageSuccess ? null : orderId;
        const orderDetailsPath = pathByRouteName('OrderDetailsPage', routes, { id: orderId.uuid });
        const initialValues = {
          initialMessageFailedToTransaction,
          savePaymentMethodFailed: !paymentMethodSaved,
        };

        initializeOrderPage(initialValues, routes, dispatch);
        clearData(STORAGE_KEY);
        history.push(orderDetailsPath);
      })
      .catch(err => {
        console.error(err);
        this.setState({ submitting: false });
      });
  }

  render() {
    return (
      <CheckoutPage
        {...this.props}
        handleSubmit={this.handleSubmit}
        dataLoaded={this.state.dataLoaded}
        submitting={this.state.submitting}
        pageData={this.state.pageData}
        checkIsPaymentExpired={checkIsPaymentExpired}
        checkIsInitiatePayment={txIsInitiatePayment}
      />
    );
  }
}

MainCheckoutPageComponent.defaultProps = {
  initiateOrderError: null,
  confirmPaymentError: null,
  listing: null,
  bookingData: {},
  bookingDates: null,
  speculateTransactionError: null,
  speculatedTransaction: null,
  transaction: null,
  currentUser: null,
  paymentIntent: null,
};

MainCheckoutPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  listing: propTypes.listing,
  bookingData: object,
  bookingDates: shape({
    bookingStart: instanceOf(Date).isRequired,
    bookingEnd: instanceOf(Date).isRequired,
  }),
  fetchStripeCustomer: func.isRequired,
  stripeCustomerFetched: bool.isRequired,
  fetchSpeculatedTransaction: func.isRequired,
  speculateTransactionInProgress: bool.isRequired,
  speculateTransactionError: propTypes.error,
  speculatedTransaction: propTypes.transaction,
  transaction: propTypes.transaction,
  currentUser: propTypes.currentUser,
  params: shape({
    id: string,
    slug: string,
  }).isRequired,
  onConfirmPayment: func.isRequired,
  onInitiateOrder: func.isRequired,
  onConfirmCardPayment: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onSendMessage: func.isRequired,
  initiateOrderError: propTypes.error,
  confirmPaymentError: propTypes.error,

  paymentIntent: object,

  // from connect
  dispatch: func.isRequired,

  // from injectIntl
  intl: intlShape.isRequired,

  // from withRouter
  history: shape({
    push: func.isRequired,
  }).isRequired,
  onCreateSetupIntent: func.isRequired,
  onHandleCardSetup: func.isRequired,
  onInitiatePayment: func.isRequired,
};

const mapStateToProps = state => {
  const {
    listing,
    bookingData,
    bookingDates,
    stripeCustomerFetched,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    transaction,
    initiateOrderError,
    confirmPaymentError,
  } = state.CheckoutPage;
  const { currentUser } = state.user;
  const { paymentIntent } = state.stripe;
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    stripeCustomerFetched,
    bookingData,
    bookingDates,
    speculateTransactionInProgress,
    speculateTransactionError,
    speculatedTransaction,
    transaction,
    listing,
    initiateOrderError,
    confirmPaymentError,
    paymentIntent,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatch,
  fetchSpeculatedTransaction: (params, transactionId) =>
    dispatch(speculateTransaction(params, transactionId)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  onInitiateOrder: (params, transactionId) => dispatch(initiateOrder(params, transactionId)),
  onConfirmCardPayment: params => dispatch(confirmCardPayment(params)),
  onConfirmPayment: params => dispatch(confirmPayment(params)),
  onSendMessage: params => dispatch(sendMessage(params)),
  onSavePaymentMethod: (stripeCustomer, stripePaymentMethodId) =>
    dispatch(savePaymentMethod(stripeCustomer, stripePaymentMethodId)),
  onCreateSetupIntent: params => dispatch(createStripeSetupIntent(params)),
  onHandleCardSetup: params => dispatch(handleCardSetup(params)),
  onSavePhoneNumber: params => dispatch(savePhoneNumber(params)),
  onInitiatePayment: (params, transactionId) => dispatch(initiatePayment(params, transactionId)),
  onSaveLast4DigitsCard: transactionId => dispatch(saveLast4DigitsCard(transactionId)),
});

const MainCheckoutPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(MainCheckoutPageComponent);

MainCheckoutPage.setInitialValues = initialValues => setInitialValues(initialValues);

MainCheckoutPage.displayName = 'CheckOutPage';

export default MainCheckoutPage;
